import React, { useEffect, useState, useCallback } from 'react';
import { Users, FileText, MessageSquare, CreditCard, TrendingUp, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface OverviewMetrics {
  total_users: number;
  total_forms: number;
  total_responses: number;
  active_subscriptions: number;
  users_last_7d: number;
  forms_last_7d: number;
  responses_last_7d: number;
}

export const AdminDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<OverviewMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_get_overview_metrics', {});
    if (signal?.aborted) return;
    if (rpcError) {
      setError(rpcError.message);
    } else {
      setMetrics(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchMetrics(controller.signal);
    return () => controller.abort();
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
        <p className="text-red-400 mb-3">Erro ao carregar métricas: {error}</p>
        <button
          onClick={() => fetchMetrics()}
          className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!metrics) return null;

  const cards = [
    { label: 'Total Usuários', value: metrics.total_users, icon: Users, sub: `+${metrics.users_last_7d} últimos 7d` },
    { label: 'Total Formulários', value: metrics.total_forms, icon: FileText, sub: `+${metrics.forms_last_7d} últimos 7d` },
    { label: 'Total Respostas', value: metrics.total_responses, icon: MessageSquare, sub: `+${metrics.responses_last_7d} últimos 7d` },
    { label: 'Assinaturas Ativas', value: metrics.active_subscriptions, icon: CreditCard, sub: null },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Admin Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(card => (
          <div
            key={card.label}
            className="bg-neutral-900/50 border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center">
                <card.icon className="w-5 h-5 text-neutral-400" />
              </div>
              <span className="text-sm text-neutral-400">{card.label}</span>
            </div>
            <div className="text-3xl font-bold text-white">{card.value.toLocaleString()}</div>
            {card.sub && (
              <div className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {card.sub}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
